import { ReactElement, useMemo, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Typography,
  Skeleton,
  Alert,
  Link as MuiLink,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import {
  DataGridPremium,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import DataGridCourseFilters from './data-grid-course-filters'
import CourseDetailModal from './course-detail-modal'
import { dataGridSx, paperInputSx } from '../styles'
import {
  courseColumns,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
  ASSIGNMENTS_URLS,
} from '../constants'

type Course = {
  id: string
  name: string
  displayName?: string
  imageName?: string
  length: number
  section?: string
  sectionID?: string
  description?: string
}

type TrainingSeries = {
  id: string
  name: string
  courseIDs: string[]
}

type LearningPath = {
  id: string
  name: string
  courseIDs: string[]
}

type CourseRow = {
  id: string
  name: string
  length: number
  category: string
}

type CourseSelectionCardProps = {
  courses: Course[]
  trainingSeries: TrainingSeries[]
  learningPaths?: LearningPath[]
  selectedCourseIDs: string[]
  onSelectionChange: (courseIDs: string[]) => void
  isLoadingCourses?: boolean
  hasAnyProgress?: boolean
  linkedServiceID?: string
  error?: string
}

export default function CourseSelectionCard({
  courses,
  trainingSeries,
  learningPaths,
  selectedCourseIDs,
  onSelectionChange,
  isLoadingCourses = false,
  hasAnyProgress = false,
  linkedServiceID,
  error,
}: CourseSelectionCardProps): ReactElement {
  let CourseToolbar = useCallback(
    () => (
      <DataGridToolbar fileName="course-selection" showQuickFilter={false} />
    ),
    [],
  )

  let [detailCourseID, setDetailCourseID] = useState<string | null>(null)
  let detailCourseName = useMemo(
    () => courses.find((c) => c.id === detailCourseID)?.name ?? '',
    [courses, detailCourseID],
  )

  let infoColumn = useMemo<GridColDef>(
    () => ({
      field: 'info',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label="View course details"
          onClick={(e) => {
            e.stopPropagation()
            setDetailCourseID(params.row.id)
          }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      ),
    }),
    [],
  )

  let [searchQuery, setSearchQuery] = useState('')
  let [categoryFilter, setCategoryFilter] = useState('')
  let [initiallySortedRows, setInitiallySortedRows] = useState<CourseRow[]>([])

  let courseRows = useMemo<CourseRow[]>(
    () =>
      courses.map((course) => ({
        id: course.id,
        name: course.name,
        length: course.length || 0,
        category: course.section || 'Uncategorized',
      })),
    [courses],
  )

  useEffect(() => {
    if (courseRows.length > 0 && initiallySortedRows.length === 0) {
      let selectedSet = new Set(selectedCourseIDs)

      let sorted = courseRows.toSorted((a, b) => {
        let aSelected = selectedSet.has(a.id)
        let bSelected = selectedSet.has(b.id)

        if (aSelected && !bSelected) return -1
        if (!aSelected && bSelected) return 1
        return a.name.localeCompare(b.name)
      })

      setInitiallySortedRows(sorted)
    }
  }, [courseRows, initiallySortedRows.length, selectedCourseIDs])

  let availableCategories = useMemo(() => {
    let categoriesSet = new Set<string>()
    courseRows.forEach((course) => categoriesSet.add(course.category))
    return Array.from(categoriesSet).toSorted((a, b) => a.localeCompare(b))
  }, [courseRows])

  let filteredCourses = useMemo(() => {
    let query = searchQuery.toLowerCase()

    return initiallySortedRows.filter((course) => {
      if (categoryFilter && course.category !== categoryFilter) return false

      if (searchQuery && !course.name.toLowerCase().includes(query))
        return false

      return true
    })
  }, [initiallySortedRows, categoryFilter, searchQuery])

  let stats = useMemo(() => {
    let selectedCourseRows = courseRows.filter((c) =>
      selectedCourseIDs.includes(c.id),
    )

    let totalHours = selectedCourseRows.reduce(
      (sum, course) => sum + course.length,
      0,
    )
    let categories = Array.from(
      new Set(selectedCourseRows.map((c) => c.category)),
    )

    return {
      courseCount: selectedCourseRows.length,
      totalHours,
      categories,
    }
  }, [courseRows, selectedCourseIDs])

  let selectedTrainingSeriesIDs = useMemo(
    () =>
      trainingSeries
        .filter((series) => {
          let validCourseIDs = series.courseIDs.filter((courseId) =>
            courses.some((course) => course.id === courseId),
          )
          return (
            validCourseIDs.length > 0 &&
            validCourseIDs.every((id) => selectedCourseIDs.includes(id))
          )
        })
        .map((series) => series.id),
    [trainingSeries, courses, selectedCourseIDs],
  )

  let selectedLearningPathIDs = useMemo(
    () =>
      learningPaths
        ?.filter((path) => {
          let validCourseIDs = path.courseIDs.filter((courseId) =>
            courses.some((course) => course.id === courseId),
          )
          return (
            validCourseIDs.length > 0 &&
            validCourseIDs.every((id) => selectedCourseIDs.includes(id))
          )
        })
        .map((path) => path.id) || [],
    [learningPaths, courses, selectedCourseIDs],
  )

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      let filteredIDs = new Set(filteredCourses.map((row) => row.id))
      let hiddenSelectedIDs = selectedCourseIDs.filter(
        (id) => !filteredIDs.has(id),
      )

      let visibleSelectedIDs =
        newSelection?.type === 'exclude'
          ? (() => {
              let excludedIDs = new Set(
                Array.from(newSelection.ids || []) as string[],
              )
              return filteredCourses
                .map((row) => row.id)
                .filter((id) => !excludedIDs.has(id))
            })()
          : (Array.from(newSelection.ids) as string[])

      onSelectionChange([
        ...new Set([...hiddenSelectedIDs, ...visibleSelectedIDs]),
      ])
    },
    [onSelectionChange, filteredCourses, selectedCourseIDs],
  )

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setCategoryFilter('')
  }, [])

  let handleTrainingSeriesChange = useCallback(
    (event: any) => {
      let newValue = event.target.value as string[]
      let added = newValue.filter(
        (id) => !selectedTrainingSeriesIDs.includes(id),
      )
      let removed = selectedTrainingSeriesIDs.filter(
        (id) => !newValue.includes(id),
      )

      let newCourseSelection = new Set(selectedCourseIDs)

      added.forEach((seriesId) => {
        let series = trainingSeries.find((ts) => ts.id === seriesId)
        if (!series) return

        let validCourseIDs = series.courseIDs.filter((courseId) =>
          courses.some((course) => course.id === courseId),
        )
        validCourseIDs.forEach((id) => newCourseSelection.add(id))
      })

      removed.forEach((seriesId) => {
        let series = trainingSeries.find((ts) => ts.id === seriesId)
        if (!series) return

        let validCourseIDs = series.courseIDs.filter((courseId) =>
          courses.some((course) => course.id === courseId),
        )
        validCourseIDs.forEach((id) => newCourseSelection.delete(id))
      })

      onSelectionChange(Array.from(newCourseSelection))
    },
    [
      selectedTrainingSeriesIDs,
      selectedCourseIDs,
      trainingSeries,
      courses,
      onSelectionChange,
    ],
  )

  let handleLearningPathChange = useCallback(
    (event: any) => {
      let newValue = event.target.value as string[]
      let added = newValue.filter((id) => !selectedLearningPathIDs.includes(id))
      let removed = selectedLearningPathIDs.filter(
        (id) => !newValue.includes(id),
      )

      let newCourseSelection = new Set(selectedCourseIDs)

      added.forEach((pathId) => {
        let path = learningPaths?.find((lp) => lp.id === pathId)
        if (!path) return

        let validCourseIDs = path.courseIDs.filter((courseId) =>
          courses.some((course) => course.id === courseId),
        )
        validCourseIDs.forEach((id) => newCourseSelection.add(id))
      })

      removed.forEach((pathId) => {
        let path = learningPaths?.find((lp) => lp.id === pathId)
        if (!path) return

        let validCourseIDs = path.courseIDs.filter((courseId) =>
          courses.some((course) => course.id === courseId),
        )
        validCourseIDs.forEach((id) => newCourseSelection.delete(id))
      })

      onSelectionChange(Array.from(newCourseSelection))
    },
    [
      selectedLearningPathIDs,
      selectedCourseIDs,
      learningPaths,
      courses,
      onSelectionChange,
    ],
  )

  return (
    <Card className="mb-6">
      <Box className="p-6">
        <Box className="flex flex-wrap items-center justify-between mb-4">
          <Typography variant="h6" component="h2">
            Select Courses
          </Typography>
          {(searchQuery || categoryFilter) && (
            <Button
              className="whitespace-nowrap"
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={handleResetFilters}
              aria-label="Reset filters"
              sx={{ height: 32 }}
            >
              Reset Filters
            </Button>
          )}
        </Box>

        <DataGridCourseFilters
          className="mb-4"
          searchQuery={searchQuery}
          categoryFilter={categoryFilter}
          availableCategories={availableCategories}
          onSearchChange={setSearchQuery}
          onCategoryChange={setCategoryFilter}
        />

        {error && (
          <Alert severity="error" className="mb-4" role="alert">
            {error}
          </Alert>
        )}

        {hasAnyProgress && (
          <Alert severity="info" className="mb-4">
            This assignment has progress by one or more users. Assignment
            courses cannot be added or removed.{' '}
            <MuiLink
              component={Link}
              to={ASSIGNMENTS_URLS.CREATE}
              state={{ linkedServiceID }}
              underline="always"
            >
              Create a new assignment
            </MuiLink>{' '}
            instead?
          </Alert>
        )}

        {trainingSeries && trainingSeries.length > 0 && (
          <Box className="mb-4">
            <FormControl fullWidth sx={paperInputSx}>
              <InputLabel id="training-series-label">
                Select Courses by Training Series
              </InputLabel>
              <Select
                labelId="training-series-label"
                multiple
                value={selectedTrainingSeriesIDs}
                onChange={handleTrainingSeriesChange}
                disabled={hasAnyProgress}
                renderValue={(selected) =>
                  `${selected.length} training series selected`
                }
                label="Select Courses by Training Series"
                MenuProps={{
                  autoFocus: false,
                  disableAutoFocusItem: true,
                  variant: 'menu',
                }}
              >
                {trainingSeries.map((series) => {
                  let validCourseIDs = series.courseIDs.filter((courseId) =>
                    courses.some((course) => course.id === courseId),
                  )

                  if (validCourseIDs.length === 0) return null

                  return (
                    <MenuItem key={series.id} value={series.id}>
                      <Checkbox
                        checked={selectedTrainingSeriesIDs.includes(series.id)}
                      />
                      <ListItemText
                        primary={`${series.name} (${validCourseIDs.length} ${validCourseIDs.length === 1 ? 'course' : 'courses'})`}
                      />
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {learningPaths && learningPaths.length > 0 && (
          <Box className="mb-4">
            <FormControl fullWidth sx={paperInputSx}>
              <InputLabel id="learning-path-label">
                Select Courses by Learning Path
              </InputLabel>
              <Select
                labelId="learning-path-label"
                multiple
                value={selectedLearningPathIDs}
                onChange={handleLearningPathChange}
                disabled={hasAnyProgress}
                renderValue={(selected) =>
                  `${selected.length} learning path${selected.length !== 1 ? 's' : ''} selected`
                }
                label="Select Courses by Learning Path"
                MenuProps={{
                  autoFocus: false,
                  disableAutoFocusItem: true,
                  variant: 'menu',
                }}
              >
                {learningPaths.map((path) => {
                  let validCourseIDs = path.courseIDs.filter((courseId) =>
                    courses.some((course) => course.id === courseId),
                  )

                  if (validCourseIDs.length === 0) return null

                  return (
                    <MenuItem key={path.id} value={path.id}>
                      <Checkbox
                        checked={selectedLearningPathIDs.includes(path.id)}
                      />
                      <ListItemText
                        primary={`${path.name} (${validCourseIDs.length} ${validCourseIDs.length === 1 ? 'course' : 'courses'})`}
                      />
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box
          className="flex flex-wrap items-center justify-end gap-2 mb-3"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <Chip
            label={`${stats.courseCount} course${stats.courseCount !== 1 ? 's' : ''} selected`}
            size="medium"
            color="primary"
            aria-label={`${stats.courseCount} ${stats.courseCount !== 1 ? 'courses' : 'course'} selected`}
          />
          <Chip
            label={`${stats.totalHours.toFixed(1)} hours`}
            size="medium"
            color="primary"
            aria-label={`Total of ${stats.totalHours.toFixed(1)} hours`}
          />
        </Box>

        {isLoadingCourses ? (
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 1 }}
            aria-label="Loading courses"
          />
        ) : (
          <DataGridPremium
            rows={filteredCourses}
            columns={[...courseColumns, infoColumn]}
            slots={{ toolbar: CourseToolbar }}
            showToolbar
            checkboxSelection={!hasAnyProgress}
            disableRowSelectionOnClick
            rowSelectionModel={{
              type: 'include',
              ids: new Set(selectedCourseIDs),
            }}
            onRowSelectionModelChange={handleSelectionChange}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
              },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            sx={dataGridSx}
            aria-label="Select courses"
          />
        )}
      </Box>
      <CourseDetailModal
        courseID={detailCourseID}
        courseName={detailCourseName}
        onClose={() => setDetailCourseID(null)}
      />
    </Card>
  )
}
